import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { supabase } from '../lib/supabase';
import { useAuthStore } from '../store/authStore';
import { acceptCoachInvitation } from '../utils/invitationUtils';
import { toast } from 'react-hot-toast';
import Logo from '../components/layout/Logo';

const AuthCallback: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const { setUser, setSession, setIsAuthenticated } = useAuthStore();

  useEffect(() => {
    handleAuthCallback();
  }, []);

  const handleAuthCallback = async () => {
    try {
      console.log('[AuthCallback] Processing OAuth callback...');
      
      // Get the session from the callback
      const { data: { session }, error: sessionError } = await supabase.auth.getSession();
      
      if (sessionError) {
        console.error('[AuthCallback] Session error:', sessionError);
        throw sessionError;
      }

      if (!session || !session.user) {
        console.error('[AuthCallback] No session or user found');
        throw new Error('No valid session found');
      }

      console.log('[AuthCallback] Successfully authenticated user:', session.user.email);

      // Update auth store
      setUser(session.user);
      setSession(session);
      setIsAuthenticated(true);

      // Check if there's an invitation to process
      const invitationId = searchParams.get('invitation');
      
      if (invitationId) {
        console.log('[AuthCallback] Processing invitation:', invitationId);
        await processInvitation(invitationId, session.user);
      } else {
        console.log('[AuthCallback] No invitation found, redirecting to home');
        toast.success('Successfully signed in!');
        navigate('/');
      }
    } catch (error: any) {
      console.error('[AuthCallback] Error processing callback:', error);
      setError(error.message || 'Failed to complete authentication');
      
      // Redirect to login with error after a delay
      setTimeout(() => {
        navigate('/login?error=auth_callback_failed');
      }, 3000);
    } finally {
      setLoading(false);
    }
  };

  const processInvitation = async (invitationId: string, user: any) => {
    try {
      console.log('[AuthCallback] Loading invitation details...');
      
      // Get the invitation details
      const { data: invitation, error: invitationError } = await supabase
        .from('coach_invitations')
        .select(`
          id,
          team_id,
          email,
          role,
          status,
          teams:team_id (
            id,
            name,
            sport
          )
        `)
        .eq('id', invitationId)
        .single();

      if (invitationError || !invitation) {
        console.error('[AuthCallback] Error loading invitation:', invitationError);
        toast.error('Invalid or expired invitation link');
        navigate('/');
        return;
      }

      if (invitation.status !== 'pending') {
        console.error('[AuthCallback] Invitation is not pending:', invitation.status);
        toast.error('This invitation has already been used');
        navigate('/');
        return;
      }

      // Check if the email matches (optional - you might want to allow different emails)
      if (invitation.email && user.email !== invitation.email) {
        console.warn('[AuthCallback] Email mismatch:', user.email, 'vs', invitation.email);
        // You can choose to continue or require email match
      }

      console.log('[AuthCallback] Processing invitation for team:', invitation.teams?.name);

      const result = await acceptCoachInvitation(invitation, user);

      if (!result.success) {
        toast.error('Signed in and invitation accepted, but there was an issue with team membership. Please contact support.');
        navigate('/');
        return;
      }

      toast.success(`Welcome to ${invitation.teams?.name}! You've successfully joined as a ${invitation.role === 'head_coach' ? 'Head Coach' : 'Assistant Coach'}.`);
      setTimeout(() => {
        window.dispatchEvent(new CustomEvent('force-team-refresh'));
      }, 500);
      navigate(`/team/${invitation.team_id}`);
    } catch (error: any) {
      console.error('[AuthCallback] Error processing invitation:', error);
      toast.error('Signed in successfully but there was an issue processing your invitation.');
      navigate('/');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex flex-col justify-center items-center bg-[#3A3C3E]">
        <div className="text-center">
          <Logo className="h-16 mx-auto mb-8" />
          <div className="w-8 h-8 border-4 border-primary border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <h2 className="text-xl font-semibold text-white mb-2">Completing sign in...</h2>
          <p className="text-gray-300">Please wait while we process your authentication.</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex flex-col justify-center items-center bg-[#3A3C3E]">
        <div className="text-center max-w-md">
          <Logo className="h-16 mx-auto mb-8" />
          <div className="bg-red-50 border border-red-200 rounded-xl p-6 mb-4">
            <h2 className="text-xl font-semibold text-red-800 mb-2">Authentication Failed</h2>
            <p className="text-red-600">{error}</p>
          </div>
          <p className="text-gray-300">You will be redirected to the login page shortly.</p>
        </div>
      </div>
    );
  }

  // This shouldn't render, but just in case
  return (
    <div className="min-h-screen flex flex-col justify-center items-center bg-[#3A3C3E]">
      <Logo className="h-16" />
      <p className="text-white mt-4">Redirecting...</p>
    </div>
  );
};

export default AuthCallback; 